import * as THREE from 'three';
import { MAP } from '../config.js';
import { simplifyLayout } from './mapLayout.js';
import { LAYOUT_STRIDE as S } from './navLayers.js';
import { buildHouse } from './house/buildHouse.js';
import { planFromLayout } from './house/planFromLayout.js';

/**
 * スキャンに CG を肉付けした表示（「CG」モード）。
 *
 * スキャンのメッシュはどうしても面が波打つので、スキャンから測った床・壁・家具の位置を
 * きれいな面に置き換える。中二階やロフトの高さ、階段の段も、スキャンで測ったものをそのまま使う。
 * 床は木目、壁は漆喰の質感。家具は何か分からないので、スキャンの色を付けた箱の集まりで置く。
 *
 * @param detailed navLayers の layout
 * @param labels   [{ name, x, y, z }] 部屋の見出しを出す位置
 * @param nav      navLayers の当たり判定マップ（天井の高さに使う）
 * @param spots    [{ x, y, z }] 視点の足元。スキャンで床が抜けていてもここには床を敷く
 */
export function createSimpleMap(detailed, { labels = [], nav = null, spots = [] } = {}) {
  const layout = simplifyLayout(detailed);
  const group = new THREE.Group();
  group.name = 'simple-map';

  const plan = planFromLayout(layout, { nav, spots });
  const house = buildHouse({ ...plan, worldUv: true });
  group.add(house);

  /** この高さより上を切り取る。普段は切らない */
  const clipPlane = new THREE.Plane(new THREE.Vector3(0, -1, 0), NO_CUT);
  /*
   * 質感（material）は名前と大きさが同じなら使い回されるので、CG の物件では
   * 物件そのものの建物と、この表示の建物が同じ material を指すことがある。
   * そこへ直接切り取り面を書き込むと、物件側の切り取り（main.js の setScanCut）を
   * 上書きしてしまい、マップ表示で上の階が切れなくなる。ここだけの複製に差し替える。
   */
  const copies = new Map();
  house.traverse((node) => {
    if (!node.isMesh) return;
    // レイキャスト（採寸・歩行の判定）はスキャンのメッシュで行うので、こちらは当てない
    node.raycast = () => {};
    const many = Array.isArray(node.material);
    const next = (many ? node.material : [node.material]).map((m) => {
      if (!copies.has(m)) {
        const copy = m.clone();
        copy.clippingPlanes = [clipPlane];
        copies.set(m, copy);
      }
      return copies.get(m);
    });
    node.material = many ? next : next[0];
  });

  const labelGroup = new THREE.Group();
  const labelSprites = labels.map((label) => {
    const made = createLabel(label.name);
    made.sprite.position.set(label.x, label.y, label.z);
    labelGroup.add(made.sprite);
    return made;
  });
  let labelFov = null;
  group.add(labelGroup);

  return {
    root: group,
    /** 組み立てに使った設計データ（調整・検証用） */
    plan,
    layout,
    /** 歩ける床の範囲の中心（マップ表示の最初の見下ろし位置） */
    center: centerOf(layout),
    /**
     * 見出しを画面の高さに対して一定の大きさにする。
     * 距離で大きさが変わらない見出しでも、視野角が狭いと拡大されるので、視野角に合わせて縮める。
     */
    updateLabels(camera) {
      if (camera.fov === labelFov) return;
      labelFov = camera.fov;
      const height = 2 * MAP.labelHeight * Math.tan((camera.fov * Math.PI) / 360);
      for (const { sprite, aspect } of labelSprites) sprite.scale.set(height * aspect, height, 1);
    },
    /** CG の部屋を出すか。マップ表示ではスキャンを上から見せ、見出しだけ使う */
    setBoxesVisible(visible) {
      house.visible = visible;
    },
    /** y より上を切り取る。null で切らない */
    setCut(y) {
      clipPlane.constant = y === null ? NO_CUT : y;
    },
    dispose() {
      house.traverse((node) => {
        if (node.isMesh) node.geometry.dispose();
      });
      // 複製した質感はこの表示だけのものなので、ここで捨てる（元の質感は使い回すので残す）
      for (const copy of copies.values()) copy.dispose();
      for (const { texture } of labelSprites) texture.dispose();
    },
  };
}

/** 部屋の名前の見出し。遠くから見ても同じ大きさで、壁の向こうでも読めるようにする */
function createLabel(text) {
  const font = 'bold 44px "Hiragino Sans", "Yu Gothic", "Meiryo", sans-serif';
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context.font = font;
  const width = Math.ceil(context.measureText(text).width) + 40;
  canvas.width = width;
  canvas.height = 72;
  context.font = font;
  context.fillStyle = 'rgba(13, 17, 23, 0.82)';
  roundRect(context, 0, 0, width, 72, 18);
  context.fill();
  context.fillStyle = '#ffffff';
  context.textBaseline = 'middle';
  context.fillText(text, 20, 38);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: texture, depthTest: false, depthWrite: false, sizeAttenuation: false }),
  );
  sprite.renderOrder = 10;
  sprite.raycast = () => {};
  return { sprite, texture, aspect: width / 72 };
}

function roundRect(context, x, y, w, h, r) {
  context.beginPath();
  context.moveTo(x + r, y);
  context.arcTo(x + w, y, x + w, y + h, r);
  context.arcTo(x + w, y + h, x, y + h, r);
  context.arcTo(x, y + h, x, y, r);
  context.arcTo(x, y, x + w, y, r);
  context.closePath();
}

function centerOf({ floors, cell, minX, minZ, baseY }) {
  let x0 = Infinity;
  let x1 = -Infinity;
  let z0 = Infinity;
  let z1 = -Infinity;
  for (let i = 0; i < floors.length; i += S) {
    x0 = Math.min(x0, floors[i]);
    x1 = Math.max(x1, floors[i]);
    z0 = Math.min(z0, floors[i + 1]);
    z1 = Math.max(z1, floors[i + 1]);
  }
  return [((x0 + x1) / 2 + 0.5) * cell + minX, baseY, ((z0 + z1) / 2 + 0.5) * cell + minZ];
}

/** 「切り取らない」の代わりの十分高い位置。Infinity はシェーダーで NaN になり得るので使わない */
const NO_CUT = 1e6;
